import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Mail,
  Building2,
  Briefcase,
  Shield,
  ChevronRight,
} from 'lucide-react';
import tenantService from '../services/tenantService';
import employeeService from '../services/employeeService';
import ingestionService from '../services/ingestionService';
import connectionService, { DirectoryUser } from '../services/connectionService';

const MAX_EMPLOYEES = 10;
const PAGE_SIZE = 25;

/** Prefer mail, fall back to a cleaned-up UPN */
const getDisplayEmail = (user: DirectoryUser): string => {
  if (user.mail) return user.mail;
  // UPN like admin@E7D8530745-xxx.onmicrosoft.com is not user-friendly
  const upn = user.userPrincipalName;
  if (upn.includes('.onmicrosoft.com')) {
    return upn.split('@')[0] + '@..onmicrosoft.com';
  }
  return upn;
};

/** If displayName is missing or looks like an email, derive a readable name */
const getDisplayName = (user: DirectoryUser): string => {
  if (user.displayName && !user.displayName.includes('@')) {
    return user.displayName;
  }
  // Use mail or UPN local part as fallback
  const email = user.mail || user.userPrincipalName;
  return email.split('@')[0];
};

const EmployeeSelection: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [directoryUsers, setDirectoryUsers] = useState<DirectoryUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ created: number; skipped: number } | null>(null);
  const [nextLink, setNextLink] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Check onboarding status + load directory
  useEffect(() => {
    const initialize = async () => {
      try {
        // Verify connection is active
        const tenant = await tenantService.getTenant();
        if (!tenant.onboardingStatus.connected) {
          navigate('/integrations', { replace: true });
          return;
        }

        // Load directory users
        const result = await connectionService.getDirectoryUsers({ pageSize: PAGE_SIZE });
        setDirectoryUsers(result.users);
        setNextLink(result.nextLink);
        setHasMore(result.nextLink !== null);
      } catch (err: any) {
        setError(err.message || 'Failed to load directory');
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [navigate]);

  const loadMoreUsers = async () => {
    if (!nextLink || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await connectionService.getDirectoryUsers({
        pageSize: PAGE_SIZE,
        nextLink,
      });
      setDirectoryUsers(prev => [...prev, ...result.users]);
      setNextLink(result.nextLink);
      setHasMore(result.nextLink !== null);
    } catch (err: any) {
      setError(err.message || 'Failed to load more users');
    } finally {
      setLoadingMore(false);
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else if (next.size < MAX_EMPLOYEES) {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAll = () => {
    const eligible = filteredUsers.filter(u => u.mailEnabled && u.accountEnabled);
    const toSelect = eligible.slice(0, MAX_EMPLOYEES);
    setSelectedUsers(new Set(toSelect.map(u => u.id)));
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const handleSubmit = async () => {
    if (selectedUsers.size === 0 || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      // Map selected directory users to batch create payload
      const usersToCreate = directoryUsers
        .filter(u => selectedUsers.has(u.id))
        .map(u => ({
          email: u.mail || u.userPrincipalName,
          displayName: u.displayName,
          externalId: u.id,
          department: u.department || undefined,
          title: u.jobTitle || undefined,
        }));

      // Batch create employees
      const result = await employeeService.batchCreateEmployees('o365', usersToCreate);
      setSubmitResult({
        created: result.created.length,
        skipped: result.skipped.length,
      });

      // Start ingestion for each created employee
      const allEmployeeIds = [
        ...result.created.map(e => e.employee_id),
        ...result.skipped.map(e => e.employeeId),
      ].filter(Boolean);

      if (allEmployeeIds.length > 0) {
        // Start ingestion one at a time (MVP limit: 1 per request)
        for (const employeeId of allEmployeeIds) {
          try {
            await ingestionService.startIngestion([employeeId]);
          } catch (ingestionErr: any) {
            console.error(`Failed to start ingestion for ${employeeId}:`, ingestionErr.message);
          }
        }
      }

      // Navigate to ingestion status after brief delay
      setTimeout(() => {
        navigate('/ingestion-status');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to create employees');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter users by search query
  const filteredUsers = directoryUsers.filter(user => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      getDisplayName(user).toLowerCase().includes(q) ||
      (user.mail && user.mail.toLowerCase().includes(q)) ||
      user.userPrincipalName.toLowerCase().includes(q) ||
      (user.department && user.department.toLowerCase().includes(q)) ||
      (user.jobTitle && user.jobTitle.toLowerCase().includes(q))
    );
  });

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-niwt-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading directory users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-libre">Select Employees</h1>
          <p className="text-gray-600 mt-1">
            Choose up to {MAX_EMPLOYEES} employees from your Microsoft 365 directory to create digital twins
          </p>
        </div>
        <button
          onClick={() => navigate('/integrations')}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Integrations
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Success banner */}
      {submitResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Employees Created</p>
            <p className="text-sm text-green-700 mt-1">
              {submitResult.created} new {submitResult.created === 1 ? 'employee' : 'employees'} created
              {submitResult.skipped > 0 && `, ${submitResult.skipped} already existed`}.
              Starting email ingestion...
            </p>
          </div>
        </div>
      )}

      {/* Search + Selection info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, department..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-niwt-primary focus:border-transparent text-sm"
            />
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${selectedUsers.size >= MAX_EMPLOYEES ? 'text-amber-600' : 'text-gray-600'}`}>
              {selectedUsers.size}/{MAX_EMPLOYEES} selected
            </span>
            <button
              onClick={selectAll}
              disabled={selectedUsers.size >= MAX_EMPLOYEES}
              className="px-3 py-1.5 text-xs font-medium text-niwt-primary bg-purple-50 rounded-md hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Select All
            </button>
            <button
              onClick={clearSelection}
              disabled={selectedUsers.size === 0}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Table header */}
        <div className="hidden md:grid md:grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div className="col-span-1"></div>
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Department</div>
          <div className="col-span-2">Job Title</div>
          <div className="col-span-1">Status</div>
        </div>

        {/* User rows */}
        <div className="divide-y divide-gray-100 max-h-[calc(100vh-500px)] overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {searchQuery ? 'No users match your search' : 'No directory users found'}
              </p>
            </div>
          ) : (
            filteredUsers.map(user => {
              const isSelected = selectedUsers.has(user.id);
              const isDisabled = !user.mailEnabled || !user.accountEnabled;
              const canSelect = !isDisabled && (isSelected || selectedUsers.size < MAX_EMPLOYEES);

              return (
                <div
                  key={user.id}
                  onClick={() => canSelect && toggleUser(user.id)}
                  className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-4 transition-colors ${
                    isDisabled
                      ? 'opacity-50 cursor-not-allowed bg-gray-50'
                      : isSelected
                      ? 'bg-purple-50 hover:bg-purple-100 cursor-pointer'
                      : canSelect
                      ? 'hover:bg-gray-50 cursor-pointer'
                      : 'opacity-70 cursor-not-allowed'
                  }`}
                >
                  {/* Checkbox */}
                  <div className="hidden md:flex col-span-1 items-center">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-niwt-primary border-niwt-primary'
                        : isDisabled
                        ? 'bg-gray-100 border-gray-300'
                        : 'bg-white border-gray-300'
                    }`}>
                      {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                    </div>
                  </div>

                  {/* Name */}
                  <div className="md:col-span-3 flex items-center space-x-2 min-w-0">
                    <div className="md:hidden">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'bg-niwt-primary border-niwt-primary'
                          : 'bg-white border-gray-300'
                      }`}>
                        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 truncate">{getDisplayName(user)}</span>
                  </div>

                  {/* Email */}
                  <div className="md:col-span-3 flex items-center min-w-0">
                    <Mail className="w-3.5 h-3.5 text-gray-400 mr-1.5 flex-shrink-0 md:hidden" />
                    <span className="text-sm text-gray-600 truncate" title={user.mail || user.userPrincipalName}>
                      {getDisplayEmail(user)}
                    </span>
                  </div>

                  {/* Department */}
                  <div className="md:col-span-2 flex items-center min-w-0">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 mr-1.5 flex-shrink-0 md:hidden" />
                    <span className="text-sm text-gray-500 truncate">{user.department || '—'}</span>
                  </div>

                  {/* Job Title */}
                  <div className="md:col-span-2 flex items-center min-w-0">
                    <Briefcase className="w-3.5 h-3.5 text-gray-400 mr-1.5 flex-shrink-0 md:hidden" />
                    <span className="text-sm text-gray-500 truncate">{user.jobTitle || '—'}</span>
                  </div>

                  {/* Status */}
                  <div className="md:col-span-1 flex items-center">
                    {isDisabled ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                        Disabled
                      </span>
                    ) : user.licensed ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
                        Licensed
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700">
                        Unlicensed
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Load more */}
        {hasMore && (
          <div className="p-4 border-t border-gray-200 text-center">
            <button
              onClick={loadMoreUsers}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-niwt-primary bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  Load More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-4 h-4 text-gray-400" />
            <p className="text-xs text-gray-500">
              Employee data is encrypted at rest and in transit. Only metadata is stored — email content is processed via RAG pipeline.
            </p>
          </div>
          <button
            onClick={handleSubmit}
            disabled={selectedUsers.size === 0 || submitting}
            className="inline-flex items-center px-6 py-2.5 bg-niwt-primary text-white font-medium text-sm rounded-lg hover:bg-niwt-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Niwts...
              </>
            ) : (
              <>
                Create Niwts & Start Ingestion
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeSelection;
